<template>
  <div class="pb-10">
    <!-- En-tete -->
    <div class="flex items-center justify-between mb-5">
      <div class="flex items-center gap-3">
        <button
          class="w-9 h-9 flex items-center justify-center border border-gray-200 rounded-lg text-gray-400 hover:border-primary hover:text-primary transition-colors"
          @click="$router.push({ name: 'sales' })">
          <i class="fas fa-arrow-left text-sm"></i>
        </button>
        <div>
          <h1 class="text-lg font-semibold text-gray-900">Details de la vente</h1>
          <p v-if="sale.invoice_number" class="text-xs text-gray-400">{{ sale.invoice_number }}</p>
        </div>
      </div>
      <div class="flex items-center gap-2">
        <button
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          @click="previewInvoice" :disabled="isDownloading">
          <i class="fas fa-eye text-[11px]"></i>
          Apercu
        </button>
        <button
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          @click="downloadInvoice" :disabled="isDownloading">
          <i class="fas fa-download text-[11px]"></i>
          PDF
        </button>
        <button
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          @click="printInvoice">
          <i class="fas fa-print text-[11px]"></i>
          Imprimer
        </button>
        <button
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors"
          @click="printSaleTicket" :disabled="isPrintingTicket || !sale.id">
          <i v-if="isPrintingTicket" class="fas fa-spinner fa-spin text-[11px]"></i>
          <i v-else class="fas fa-receipt text-[11px]"></i>
          Ticket
        </button>
      </div>
    </div>

    <!-- Loader -->
    <div v-if="loading" class="bg-white rounded-xl border border-gray-200 p-16 flex flex-col items-center justify-center">
      <div class="w-8 h-8 border-[3px] border-gray-200 border-t-primary rounded-full animate-spin mb-3"></div>
      <p class="text-sm text-gray-400">Chargement...</p>
    </div>

    <!-- Contenu principal -->
    <div v-else-if="sale.id" class="grid grid-cols-1 lg:grid-cols-3 gap-5">

      <!-- ===== COLONNE GAUCHE (2/3) ===== -->
      <div class="lg:col-span-2 space-y-5">

        <!-- Carte info vente -->
        <div class="bg-white rounded-xl border border-gray-200 p-5">
          <div class="flex items-start justify-between mb-5">
            <div>
              <h2 class="text-base font-bold text-gray-900 font-mono">{{ sale.invoice_number }}</h2>
              <p class="text-xs text-gray-400 mt-0.5">{{ formatDate(sale.sale_date) }}</p>
            </div>
            <SaleStatusBadge :sale="sale" />
          </div>

          <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-0.5">Type</p>
              <p class="text-sm font-medium text-gray-900">{{ getSaleTypeLabel(sale.type) }}</p>
            </div>
            <div>
              <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-0.5">Client</p>
              <p class="text-sm font-medium text-gray-900">{{ sale.customer?.name || 'Client anonyme' }}</p>
            </div>
            <div>
              <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-0.5">Vendeur</p>
              <p class="text-sm font-medium text-gray-900">{{ sale.user?.name || 'N/A' }}</p>
            </div>
            <div v-if="sale.payments && sale.payments.length > 0">
              <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-0.5">Paiement</p>
              <p class="text-sm font-medium text-gray-900">{{ getPaymentMethodName(sale.payments[0]) }}</p>
            </div>
          </div>
        </div>

        <!-- Tableau produits -->
        <div class="bg-white rounded-xl border border-gray-200">
          <div class="px-5 py-4 border-b border-gray-100">
            <h3 class="text-sm font-semibold text-gray-700">Articles <span class="text-gray-400 font-normal">({{ sale.sale_items?.length || 0 }})</span></h3>
          </div>

          <div class="divide-y divide-gray-50">
            <div v-for="item in sale.sale_items" :key="item.id"
              class="flex items-center gap-4 px-5 py-3 hover:bg-gray-50/60 transition-colors">
              <!-- Image -->
              <div class="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center flex-shrink-0">
                <img v-if="item.product.image_url" :src="item.product.image_url" :alt="item.product.name"
                  class="w-full h-full object-cover" />
                <i v-else class="fas fa-image text-gray-300 text-xs"></i>
              </div>
              <!-- Nom + prix unitaire -->
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                  <span class="truncate">{{ item.product.name }}</span>
                  <span v-if="item.note"
                    class="relative group inline-flex items-center justify-center w-4 h-4 rounded bg-red-50 cursor-help flex-shrink-0"
                    :aria-label="`Note: ${item.note}`">
                    <i class="fas fa-exclamation text-[9px] text-red-400"></i>
                    <span
                      class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-normal max-w-xs opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                      {{ item.note }}
                    </span>
                  </span>
                </p>
                <p class="text-xs text-gray-400">{{ formatCurrency(item.unit_price) }} x {{ item.quantity }}</p>
              </div>
              <!-- Total -->
              <div class="text-sm font-bold text-gray-900 flex-shrink-0">
                {{ formatCurrency(item.total_amount) }}
              </div>
            </div>
          </div>

          <!-- Totaux -->
          <div class="px-5 py-4 border-t border-gray-100 space-y-2">
            <div v-if="parseFloat(sale.discount) > 0" class="flex justify-between text-sm">
              <span class="text-gray-500">Remise</span>
              <span class="font-medium text-red-500">-{{ formatCurrency(sale.discount) }}</span>
            </div>
            <div class="flex justify-between items-center pt-2 border-t border-gray-100">
              <span class="text-sm font-bold text-gray-900">Total TTC</span>
              <span class="text-lg font-bold text-gray-900">{{ formatCurrency(sale.total_incl_tax) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== COLONNE DROITE (1/3) ===== -->
      <div class="space-y-5">

        <!-- Facture associee -->
        <div v-if="sale.sale_invoice" class="bg-white rounded-xl border border-gray-200 p-5">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-sm font-semibold text-gray-700">Facture associee</h3>
            <span :class="[
              'inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold',
              getInvoiceStatusClass(sale.sale_invoice.status)
            ]">
              {{ getInvoiceStatusLabel(sale.sale_invoice.status) }}
            </span>
          </div>

          <div class="space-y-2.5">
            <div class="flex justify-between items-center">
              <span class="text-xs text-gray-400">N Facture</span>
              <span class="text-sm font-semibold text-gray-900 font-mono">{{ sale.sale_invoice.invoice_number }}</span>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-xs text-gray-400">Montant total</span>
              <span class="text-sm font-medium text-gray-900">{{ formatCurrency(sale.sale_invoice.total_amount) }}</span>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-xs text-gray-400">Montant paye</span>
              <span class="text-sm font-medium text-green-600">{{ formatCurrency(sale.sale_invoice.paid_amount) }}</span>
            </div>
            <div v-if="sale.sale_invoice.total_amount - sale.sale_invoice.paid_amount > 0" class="flex justify-between items-center">
              <span class="text-xs text-gray-400">Reste a payer</span>
              <span class="text-sm font-medium text-red-500">{{ formatCurrency(sale.sale_invoice.total_amount - sale.sale_invoice.paid_amount) }}</span>
            </div>
          </div>

          <button class="w-full mt-4 pt-3 border-t border-gray-100 text-sm font-medium text-primary hover:text-primary-dark transition-colors text-center"
            @click="viewSaleInvoice">
            Voir la facture →
          </button>
        </div>

        <!-- Resume paiement -->
        <div v-if="!sale.sale_invoice" class="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <!-- Status banner -->
          <div :class="[
            'px-5 py-3 border-b',
            sale.status === 'paid' ? 'bg-green-50 border-green-100' :
            sale.status === 'partial_paid' ? 'bg-blue-50 border-blue-100' :
            'bg-amber-50 border-amber-100'
          ]">
            <div class="flex items-center justify-between">
              <h3 :class="[
                'text-sm font-semibold',
                sale.status === 'paid' ? 'text-green-700' :
                sale.status === 'partial_paid' ? 'text-blue-700' :
                'text-amber-700'
              ]">{{ getPaymentTitle }}</h3>
              <span v-if="sale.payments && sale.payments.length > 0"
                class="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-white/60 text-gray-500">
                {{ sale.payments.length }} paiement(s)
              </span>
            </div>
          </div>

          <div class="p-5 space-y-4">
            <!-- Montants -->
            <div class="space-y-2">
              <div class="flex justify-between items-center">
                <span class="text-xs text-gray-400">Total vente</span>
                <span class="text-sm font-semibold text-gray-900">{{ formatCurrency(sale.total_incl_tax) }}</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-xs text-gray-400">Paye</span>
                <span class="text-sm font-semibold text-green-600">{{ formatCurrency(paidAmount) }}</span>
              </div>
              <div v-if="remainingAmount > 0" class="flex justify-between items-center pt-2 border-t border-gray-100">
                <span class="text-xs font-medium text-gray-500">Reste a payer</span>
                <span class="text-sm font-bold text-red-500">{{ formatCurrency(remainingAmount) }}</span>
              </div>
            </div>

            <!-- Historique paiements -->
            <div v-if="sale.payments && sale.payments.length > 0">
              <p class="text-[10px] text-gray-400 uppercase tracking-wider mb-2">Historique</p>
              <div class="space-y-1 max-h-28 overflow-y-auto">
                <div v-for="payment in sale.payments" :key="payment.id"
                  class="flex items-center justify-between py-1.5 px-2.5 bg-gray-50 rounded-lg text-xs">
                  <div class="flex items-center gap-2">
                    <span class="font-semibold text-gray-900">{{ formatCurrency(payment.amount) }}</span>
                    <span class="text-gray-400">{{ getPaymentMethodName(payment) }}</span>
                  </div>
                  <span class="text-gray-300">{{ formatShortDate(payment.payment_date) }}</span>
                </div>
              </div>
            </div>

            <!-- Boutons -->
            <div class="space-y-2 pt-1">
              <button v-if="canMakePayment" v-permission="'payments'"
                class="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-primary hover:bg-primary-dark text-white text-sm font-medium rounded-lg transition-colors"
                @click="openPaymentModal">
                <i class="fas fa-credit-card text-xs"></i>
                {{ remainingAmount > 0 && paidAmount > 0 ? 'Completer le paiement' : 'Encaisser la vente' }}
              </button>

              <button v-if="sale.status === 'paid' && sale.payments && sale.payments.length > 0"
                class="w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                @click="downloadPaymentReceipt" :disabled="isDownloadingReceipt">
                <i v-if="isDownloadingReceipt" class="fas fa-spinner fa-spin text-xs"></i>
                <i v-else class="fas fa-download text-xs"></i>
                Telecharger le recu
              </button>
            </div>
          </div>
        </div>

        <!-- Actions rapides -->
        <div class="bg-white rounded-xl border border-gray-200 p-5">
          <h3 class="text-sm font-semibold text-gray-700 mb-3">Actions</h3>

          <div class="space-y-1.5">
            <button v-if="sale.status != 'refunded'" v-permission="'sales.refund'"
              class="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              @click="showRefundModal = true">
              <i class="fas fa-rotate-left text-xs w-4 text-center"></i>
              Rembourser
            </button>
            <button v-permission="'make-sale'"
              class="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
              @click="duplicateSale">
              <i class="fas fa-copy text-xs w-4 text-center"></i>
              Dupliquer
            </button>
            <button
              class="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
              @click="sendBySMS">
              <i class="fas fa-comment-sms text-xs w-4 text-center"></i>
              Envoyer par SMS
            </button>
            <button
              class="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
              @click="sendByEmail">
              <i class="fas fa-envelope text-xs w-4 text-center"></i>
              Envoyer par Email
            </button>
            <button
              class="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
              @click="sendByWhatsApp">
              <i class="fab fa-whatsapp text-xs w-4 text-center"></i>
              WhatsApp
            </button>
          </div>
        </div>

        <!-- Remboursements -->
        <div v-if="sale.refunds && sale.refunds.length > 0" class="bg-white rounded-xl border border-gray-200 p-5">
          <h3 class="text-sm font-semibold text-gray-700 mb-3">Remboursements</h3>

          <div class="space-y-2">
            <div v-for="refund in sale.refunds" :key="refund.id"
              class="flex items-center justify-between p-3 bg-red-50/50 rounded-lg">
              <div>
                <span class="text-sm font-bold text-red-600">{{ formatCurrency(refund.amount) }}</span>
                <p class="text-[11px] text-gray-400 mt-0.5">{{ formatDate(refund.refund_date) }}</p>
              </div>
              <span class="text-xs text-gray-500 bg-white px-2 py-0.5 rounded-full">
                {{ getPaymentMethodName({ payment_type: refund.payment_type }) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Vente introuvable -->
    <div v-else-if="!loading && !sale.id" class="bg-white rounded-xl border border-gray-200 py-16 px-6 text-center">
      <div class="text-gray-300 mb-4">
        <i class="fas fa-exclamation-circle text-4xl"></i>
      </div>
      <h2 class="text-base font-semibold text-gray-700 mb-1">Vente introuvable</h2>
      <p class="text-sm text-gray-400 mb-6">La vente que vous recherchez n'existe pas.</p>
      <button
        class="px-5 py-2.5 bg-primary hover:bg-primary-dark text-white text-sm font-medium rounded-lg transition-colors"
        @click="$router.push({ name: 'sales' })">
        Retour aux ventes
      </button>
    </div>

    <!-- Modales -->
    <RefundModal :show="showRefundModal" :sale="sale" :cash-registers="cashRegisters" @close="showRefundModal = false"
      @refunded="onRefundSuccess" />
    <PaymentModal :show="showPaymentModal" :sale="sale" :customer="sale.customer" :wallet="sale.customer?.wallet"
      @close="showPaymentModal = false" @payment-success="onPaymentSuccess" />
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { salesService } from '@/services'
import CashRegisterService from '@/services/cashRegisterService'
import paymentService from '@/services/paymentService'
import thermalPrinterService from '@/services/thermalPrinterService'
import RefundModal from './RefundModal.vue'
import PaymentModal from './PaymentModal.vue'
import { useToastStore } from '@/store/modules/toastStore'
import SaleStatusBadge from '@/components/sales/SaleStatusBadge.vue'

export default defineComponent({
  name: 'SaleDetailsView',
  components: {
    RefundModal,
    PaymentModal,
    SaleStatusBadge
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const toastStore = useToastStore()

    const sale = ref({})
    const loading = ref(true)
    const showRefundModal = ref(false)
    const showPaymentModal = ref(false)
    const isDownloading = ref(false)
    const isDownloadingReceipt = ref(false)
    const isPrintingTicket = ref(false)
    const cashRegisters = ref([])

    const paidAmount = computed(() => {
      if (!sale.value.payments || sale.value.payments.length === 0) return 0
      return sale.value.payments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0)
    })

    const remainingAmount = computed(() => {
      return (sale.value.total_incl_tax || 0) - paidAmount.value
    })

    const canMakePayment = computed(() => {
      const allowedStatuses = ['pending', 'partial_paid']
      return allowedStatuses.includes(sale.value.status) && !sale.value.sale_invoice
    })

    const getPaymentTitle = computed(() => {
      if (sale.value.status === 'paid') return 'Vente payee'
      if (sale.value.status === 'partial_paid') return 'Paiement partiel'
      return 'En attente de paiement'
    })

    const loadSaleDetails = async () => {
      const saleId = route.params.id
      if (!saleId) {
        router.push({ name: 'sales' })
        return
      }

      try {
        loading.value = true
        const response = await salesService.getSale(saleId, {
          with: ['user', 'saleItems', 'customer', 'saleItems.product', 'payments', 'saleInvoice']
        })
        sale.value = response.data.data
      } catch (err) {
        console.error('Erreur:', err)
        sale.value = {}
      } finally {
        loading.value = false
      }
    }

    const loadCashRegisters = async () => {
      try {
        const response = await CashRegisterService.getCashRegisters({
          status: 'open',
          per_page: 100
        })
        cashRegisters.value = response.data || []
      } catch (err) {
        console.error('Erreur chargement caisses:', err)
        cashRegisters.value = []
      }
    }

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'XOF',
        minimumFractionDigits: 0
      }).format(amount || 0)
    }

    const formatDate = (dateString) => {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const formatShortDate = (dateString) => {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit'
      })
    }

    const getSaleTypeLabel = (type) => {
      return type === 'deferred' ? 'Vente differee' : 'Vente comptant'
    }

    const getInvoiceStatusLabel = (status) => {
      const labels = {
        'pending': 'En attente',
        'partial': 'Partielle',
        'paid': 'Payee'
      }
      return labels[status] || status
    }

    const getInvoiceStatusClass = (status) => {
      const classes = {
        'pending': 'bg-amber-50 text-amber-700',
        'partial': 'bg-blue-50 text-blue-700',
        'paid': 'bg-green-50 text-green-700'
      }
      return classes[status] || 'bg-gray-100 text-gray-600'
    }

    const viewSaleInvoice = () => {
      if (sale.value.sale_invoice?.id) {
        router.push({ name: 'sale-invoice-details', params: { id: sale.value.sale_invoice.id } })
      }
    }

    const getPaymentMethodName = (payment) => {
      if (!payment) return 'N/A'
      const types = {
        'cash': 'Especes',
        'card': 'Carte',
        'mobile_money': 'Mobile Money',
        'orange_money': 'Orange Money',
        'wave': 'Wave',
        'check': 'Cheque',
        'bank_transfer': 'Virement',
        'other': 'Autre'
      }
      return types[payment.payment_type] || payment.payment_type
    }

    const downloadInvoice = async () => {
      if (isDownloading.value) return
      try {
        isDownloading.value = true
        await salesService.downloadInvoice(sale.value.id, false)
      } catch (err) {
        console.error('Erreur telechargement:', err)
      } finally {
        isDownloading.value = false
      }
    }

    const previewInvoice = async () => {
      if (isDownloading.value) return
      try {
        isDownloading.value = true
        await salesService.downloadInvoice(sale.value.id, true)
      } catch (err) {
        console.error('Erreur apercu:', err)
      } finally {
        isDownloading.value = false
      }
    }

    const printInvoice = () => {
      console.log('TODO: Imprimer la facture', sale.value.id)
    }

    const printSaleTicket = async () => {
      if (isPrintingTicket.value) return
      if (!sale.value || !sale.value.id) return
      try {
        isPrintingTicket.value = true
        await thermalPrinterService.printTicket(sale.value, {
          connectionType: 'usb',
          shopInfo: JSON.parse(localStorage.getItem('company'))
        })
        toastStore.addToast({
          type: 'success',
          message: 'Ticket envoye a l\'imprimante',
          duration: 3000
        })
      } catch (error) {
        console.error('Erreur impression ticket:', error)
        toastStore.addToast({
          type: 'error',
          message: 'Impossible d\'imprimer le ticket',
          duration: 4000
        })
      } finally {
        isPrintingTicket.value = false
      }
    }

    const openPaymentModal = () => {
      showPaymentModal.value = true
    }

    const onPaymentSuccess = async () => {
      showPaymentModal.value = false
      await loadSaleDetails()
      toastStore.addToast({
        type: 'success',
        message: 'Paiement enregistre avec succes',
        duration: 4000
      })
    }

    const duplicateSale = () => {
      console.log('TODO: Dupliquer la vente', sale.value.id)
    }

    const sendBySMS = () => {
      console.log('TODO: Envoyer par SMS', sale.value.id)
    }

    const sendByEmail = () => {
      console.log('TODO: Envoyer par Email', sale.value.id)
    }

    const sendByWhatsApp = () => {
      console.log('TODO: Envoyer par WhatsApp', sale.value.id)
    }

    const onRefundSuccess = async () => {
      showRefundModal.value = false
      await loadSaleDetails()
      toastStore.addToast({
        type: 'success',
        message: 'Remboursement effectue avec succes',
        duration: 4000
      })
    }

    const downloadPaymentReceipt = async () => {
      if (!sale.value.payments || sale.value.payments.length === 0) {
        toastStore.addToast({
          type: 'error',
          message: 'Aucun paiement trouve pour cette vente',
          duration: 4000
        })
        return
      }

      try {
        isDownloadingReceipt.value = true
        const lastPayment = sale.value.payments[sale.value.payments.length - 1]
        await paymentService.printPaymentReceipt(lastPayment.id, false)
        toastStore.addToast({
          type: 'success',
          message: 'Recu de paiement telecharge avec succes',
          duration: 3000
        })
      } catch (error) {
        console.error('Erreur telechargement recu:', error)
        toastStore.addToast({
          type: 'error',
          message: 'Erreur lors du telechargement du recu',
          duration: 4000
        })
      } finally {
        isDownloadingReceipt.value = false
      }
    }

    onMounted(() => {
      loadSaleDetails()
      loadCashRegisters()
    })

    return {
      sale,
      loading,
      showRefundModal,
      showPaymentModal,
      isDownloading,
      isDownloadingReceipt,
      isPrintingTicket,
      cashRegisters,
      paidAmount,
      remainingAmount,
      canMakePayment,
      getPaymentTitle,
      formatCurrency,
      formatDate,
      formatShortDate,
      getSaleTypeLabel,
      getInvoiceStatusLabel,
      getInvoiceStatusClass,
      viewSaleInvoice,
      getPaymentMethodName,
      downloadInvoice,
      previewInvoice,
      printInvoice,
      printSaleTicket,
      openPaymentModal,
      onPaymentSuccess,
      downloadPaymentReceipt,
      duplicateSale,
      sendBySMS,
      sendByEmail,
      sendByWhatsApp,
      onRefundSuccess
    }
  }
})
</script>
